import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import { Eye, EyeOff, Gamepad2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dc2ea8e9-88c1-4191-a09e-3c70ac39f874/artifacts/lxlb74jo_IMG_9876.jpeg";
const ACCENT = "#4d7cff";

const Login = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/lobby");
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.force_password_change) {
        toast.info("Please set a new password to continue.");
        navigate("/force-password-change");
        return;
      }
      toast.success("Welcome back!");
      navigate("/lobby");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "Too many login attempts. Please wait a few minutes and try again.");
      } else if (status === 401) {
        toast.error(detail || "Invalid email or password");
      } else if (!error.response) {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error(detail || "Invalid email or password");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen text-white flex flex-col items-center justify-center px-4 relative overflow-hidden" style={{ background: 'radial-gradient(circle at top, #151b3a 0%, #050814 55%, #02030a 100%)' }}>
      {/* Subtle grid */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(77,124,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77,124,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      {/* Top nav */}
      <div className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(5,8,20,0.92)', borderColor: 'rgba(120,150,255,0.25)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo-link">
            <img src={LOGO_URL} alt="JackRoyals" className="w-10 h-10 rounded-xl object-cover" />
          </Link>
          <Link to="/" className="text-[#8fa2d6] hover:text-white transition-colors font-medium text-sm" data-testid="login-back-link">Back to Home</Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md pt-24 pb-12">
        <motion.div className="flex flex-col items-center mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {/* Token visual */}
          <div className="relative w-32 h-32 mb-6">
            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle, rgba(77,124,255,0.3) 0%, transparent 70%)', boxShadow: `0 0 60px ${ACCENT}30` }} />
            <div className="absolute inset-0 rounded-full border border-dashed animate-[spin_20s_linear_infinite]" style={{ borderColor: 'rgba(120,150,255,0.3)' }} />
            <div className="absolute inset-[15%] rounded-full border border-dashed animate-[spin_15s_linear_infinite_reverse]" style={{ borderColor: 'rgba(120,150,255,0.2)' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_a6f5b0e0-0ce2-42cf-b98c-d24c018b20f3/artifacts/ygrxteyk_IMG_9872.jpeg" alt="JR Coin" className="w-20 h-20 rounded-full object-cover drop-shadow-[0_0_25px_rgba(77,124,255,0.5)]" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" data-testid="login-heading" style={{ color: '#ffffff', textShadow: `0 0 20px ${ACCENT}40` }}>Welcome Back</h1>
        </motion.div>

        {/* Login Card */}
        <motion.div className="rounded-2xl border p-6 sm:p-8" style={{ background: 'rgba(12,18,45,0.96)', borderColor: 'rgba(120,150,255,0.25)', boxShadow: `0 0 20px ${ACCENT}10` }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} data-testid="login-card">
          <p className="text-[#8fa2d6] text-sm text-center mb-6">Log in to your Jack Royals Arcade account</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input type="text" placeholder="Email or Username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 rounded-lg text-white placeholder:text-[#5a6b8a]" style={{ background: '#0a0f1f', borderColor: '#1f2a4d' }} data-testid="login-email-input" />
            <div className="relative">
              <Input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 rounded-lg pr-10 text-white placeholder:text-[#5a6b8a]" style={{ background: '#0a0f1f', borderColor: '#1f2a4d' }} data-testid="login-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6b8a] hover:text-[#c7d3ff]" tabIndex={-1}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => navigate("/?forgot=1")} className="text-xs font-medium hover:underline" style={{ color: ACCENT }} data-testid="forgot-password-link">Forgot Password?</button>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 text-white font-black text-base uppercase tracking-wider rounded-full" style={{ background: `linear-gradient(135deg, ${ACCENT}, #7f5dff)`, boxShadow: `0 0 16px ${ACCENT}70` }} data-testid="login-submit-btn">
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </form>
        </motion.div>

        <motion.p className="text-center text-[#5a6b8a] text-sm mt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          U.S. Sweepstakes Compliant &bull; No Purchase Necessary &bull; 18+
        </motion.p>
        <motion.p className="text-center text-[#5a6b8a] text-sm mt-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          Don't have an account?{" "}
          <Link to="/signup" className="font-semibold hover:underline" style={{ color: ACCENT }} data-testid="login-signup-link">Sign Up Free</Link>
        </motion.p>
      </div>
    </div>
  );
};

export default Login;
